import { Expense, ItemSplit, SplitTypeEnum, TripFolder } from "@finway-group/shared/lib/models"
import moment from "moment"

import { fetchVendor } from "Shared/queries/vendors/vendor.queries"
import { ApprovalProcessService, EmployeeService, OcrService } from "Shared/services"
import NotificationService, { NotificationDurationSec, NotificationTypeEnum } from "Shared/services/notification.service"
import { updateExpense } from "Shared/store/actions/expense/expenseActions"
import { checkIsValidationError, extractErrors, getExpenseValidationErrorTip } from "Shared/utils/errorHandler.utils"
import { buildUpdateDataAccordingToSetting, deleteUnnecessaryExpenseFormData, isHospitalityExpense, syncExpenseSplits } from "Shared/utils/expense.utils"
import { FillFormWithOcrDataParams, OnUpdateExpenseFromOcrParams, UpdatedOcrObject } from "Shared/utils/ocr.utils.types"

// TODO: these hooks should be refactored to act as real hooks, e.g. using useDispatch instead of passing dispatch into the function
export const onUpdateExpenseFromOcr = async ({ updatedOcrObject, activeExpense, taxes, t, isOcrItemizationEnabled, company, expense, dispatch }: OnUpdateExpenseFromOcrParams) => {
    try {
        const users = await EmployeeService.fetchAllEmployees()
        const updateData = await OcrService.buildOcrUpdateData({
            ocrObject: updatedOcrObject,
            existingData: activeExpense,
            taxes,
            t,
            ocrItemizationEnabled: isOcrItemizationEnabled,
        })
        const sanitizedUpdateData = deleteUnnecessaryExpenseFormData(updateData)

        // check for vendor rule to adjust dueOn if needed
        const vendor = await fetchVendor(activeExpense?.vendor?._id)
        const paymentTerm = vendor?.rule?.paymentTerm
        const dueDateUpdate =
            !sanitizedUpdateData.invoiceDueDate && paymentTerm && sanitizedUpdateData.invoiceDate
                ? { invoiceDueDate: moment(sanitizedUpdateData.invoiceDate).add(paymentTerm, "days") }
                : {}
        const newExpense = new Expense({ ...activeExpense, ...sanitizedUpdateData, ...dueDateUpdate, skipEmail: true, isOcrUpdate: true })
        let updatedExpense = syncExpenseSplits(newExpense, newExpense.splitType)

        // Rebuild approval process with ocr changes. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
        if (!updatedExpense.folderId) {
            const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                previousExpense: expense,
                awaitConfirmation: true,
                ocrConfirmation: true,
            })
            if (!approvalProcesses) {
                return
            }
            updatedExpense.approvalProcesses = approvalProcesses
        } else {
            const folder = expense as TripFolder
            const isFolderAmountChangeApproved = await ApprovalProcessService.confirmFolderAmountChange(
                updatedExpense.totalGrossPrice - activeExpense.totalGrossPrice,
                folder,
                company,
            )
            if (!isFolderAmountChangeApproved) {
                return
            }
        }

        if (activeExpense.vendor?._id !== updatedExpense.vendor?._id) {
            const currentVendor = await fetchVendor(updatedExpense.vendor?._id)
            const updatedExpenseAccordingToSetting = buildUpdateDataAccordingToSetting(updatedExpense, users, company, currentVendor)

            updatedExpense = { ...updatedExpense, ...updatedExpenseAccordingToSetting }
        }
        await updateExpense(updatedExpense.id, updatedExpense)(dispatch)
        NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed_and_updated.message"), 60)
    } catch (err) {
        if (checkIsValidationError(err)) {
            const { response } = err
            const firstError = response.data.errors[0]
            const tip = getExpenseValidationErrorTip(firstError.path as keyof Expense, t)
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:error"), `${extractErrors(response)[0]} ${tip}`, NotificationDurationSec.ERROR_MESSAGE, undefined)
            return
        }
        NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:ocr.title"))
    }
}

export const fillFormWithOcrData = async ({
    formInstance,
    expense,
    ocrObject,
    taxes,
    updateExpense,
    setPrefilledVendorData,
    t,
    ocrItemizationEnabled,
}: FillFormWithOcrDataParams) => {
    const isHospitality = isHospitalityExpense(expense)
    const requester = EmployeeService.getEmployeeById(expense.requestedBy._id)
    const formFields = formInstance.getFieldsValue()
    if (!formFields.paymentOption) {
        formFields.paymentOption = expense.paymentOption
    }
    if (!formFields.kind) {
        formFields.kind = expense.kind
    }

    const updateData: UpdatedOcrObject = await OcrService.buildOcrUpdateData({
        ocrObject,
        existingData: { ...formFields, isReimbursement: expense.isReimbursement, splitType: expense.splitType, approvalProcesses: expense.approvalProcesses },
        taxes,
        t,
        ocrItemizationEnabled,
        isHospitality,
        requester,
    })

    if (ocrObject.vendor && !updateData.vendor) {
        setPrefilledVendorData({ ...ocrObject.vendor, description: t("info:filled_by_ocr") })
    }

    updateExpense(updateData)
}
